import { createReactive } from "@supergrain/kernel"
import { useCallback, useEffect, useRef, useState, type HTMLAttributes, type Ref } from "react"
import { useFocusWithin } from "react-aria"
import type { Surface } from "./surface-arbiter"
import { useUiContext } from "./unified-tiptap-ui-context"

/** The props to spread on the chrome's reveal region (a node-view wrapper element). */
export type HoverChromeProps = HTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement> }

/**
 * The last known mouse/pen position, tracked page-globally (one set of capture
 * listeners, lazily attached): while a surface is suppressed, boundary events can lie
 * (see the restore effect below), and "has the pointer moved since?" is the only
 * question that can be answered without hit-testing. `dragover` is included because
 * pointer events stop firing for the duration of a native drag — without it a drag
 * would end "stationary" at wherever the pointer was when it started. Touch is
 * ignored — a tap must not read as a lingering hover. Null until the pointer has
 * produced an event.
 */
let lastPointerPosition: { x: number; y: number } | null = null
let pointerTrackerAttached = false

/**
 * Was the user's most recent pointer interaction a touch? Touch devices can't hover, so
 * hover-revealed chrome has no way to appear on a tap; a caret-based fallback (see
 * `caretReveal` below) stands in — but only while touch is the live modality, so a mouse
 * user's caret never keeps chrome up on a card the pointer has left. A reactive object
 * (`@supergrain/kernel`) so a `tracked()` reader repaints when the modality flips; read
 * as `interactionModality.touch`. Flipped on every pointerdown by pointer type (pen counts
 * as fine like the mouse — it hovers).
 */
const interactionModality = createReactive({ touch: false })

/** How far (px) the pointer may drift and still count as "hasn't moved". */
const STATIONARY_TOLERANCE_PX = 3
function trackPointerPosition() {
  if (pointerTrackerAttached) return
  pointerTrackerAttached = true
  const record = (event: MouseEvent) => {
    if (!(event instanceof PointerEvent) || event.pointerType !== "touch") {
      lastPointerPosition = { x: event.clientX, y: event.clientY }
    }
  }
  const recordModality = (event: PointerEvent) => {
    interactionModality.touch = event.pointerType === "touch"
  }
  document.addEventListener("pointermove", record, { capture: true, passive: true })
  document.addEventListener("pointerdown", record, { capture: true, passive: true })
  document.addEventListener("pointerdown", recordModality, { capture: true, passive: true })
  document.addEventListener("dragover", record, { capture: true, passive: true })
}

/**
 * Tracks hover + focus-within for a piece of hover-revealed chrome and applies the
 * arbiter's verdict for `surface`:
 *
 * - hover reveals only while the arbiter says the surface may show
 *   (`arbiter.visible[surface]`);
 * - containing focus reveals unconditionally — focus pins chrome, a keyboard user
 *   who tabbed in must see what they reached.
 *
 * Hover is tracked with NATIVE pointerenter/pointerleave on the region element, not
 * React events: a node view's children are sibling Tiptap portals, so React's
 * synthetic enter/leave delegation never reaches this component for pointers over a
 * nested widget (or the title's own node view). The browser computes native
 * enter/leave on the real DOM tree. Touch pointers are ignored — a tap must not
 * leave sticky hover chrome behind (the same guard react-aria's useHover applies).
 *
 * Spread `chromeProps` on the reveal region and paint from `chromeVisible` (a
 * `data-chrome-visible` attribute + a dumb CSS rule). The arbiter comes from
 * the editor UI context. The `arbiter.visible` read is a tracked signal read — the
 * CALLER MUST BE a `tracked()` component, or the verdict won't repaint it.
 *
 * A third reveal fact, for touch: on a touch device there is no hover, and a tap places
 * the caret on ProseMirror's single contenteditable ROOT — an ancestor of every node-view
 * wrapper, so focus-within never fires on the region either. But the SELECTION's focus
 * node is a descendant of the tapped card's wrapper, so "the caret is in this region" is a
 * plain DOM-containment check (tracked on `selectionchange`), exactly parallel to "the
 * pointer is in this region" for hover. It stands in for hover — same gating: only while
 * touch is the live modality and the arbiter's `mayShow` permits (so a drag or a sibling
 * editor still suppresses it, and a mouse user's caret never keeps chrome up on a region
 * the pointer has left).
 */
export function useHoverChrome(surface: Surface): {
  chromeVisible: boolean
  chromeProps: HoverChromeProps
} {
  const { arbiter } = useUiContext()
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [isHovered, setHoveredState] = useState(false)
  // Mirrors isHovered for the suppression snapshot below (which must read the current
  // value inside an effect without re-running on every hover flip).
  const hoveredRef = useRef(false)
  const setHovered = useCallback((value: boolean) => {
    hoveredRef.current = value
    setHoveredState(value)
  }, [])
  useEffect(() => {
    if (!element) return
    trackPointerPosition()
    const enter = (event: PointerEvent) => {
      if (event.pointerType !== "touch") setHovered(true)
    }
    const leave = () => setHovered(false)
    element.addEventListener("pointerenter", enter)
    element.addEventListener("pointerleave", leave)
    return () => {
      element.removeEventListener("pointerenter", enter)
      element.removeEventListener("pointerleave", leave)
      setHovered(false)
    }
  }, [element, setHovered])

  const [isFocusWithin, setFocusWithin] = useState(false)
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setFocusWithin })

  // The touch reveal fact: is the selection's focus node inside this region? Tracked on
  // `selectionchange` (document-level — the only event a caret move fires). Read below
  // only while touch is the live modality; on a hover device the pointer, not the caret,
  // is the trigger.
  const [caretWithin, setCaretWithin] = useState(false)
  useEffect(() => {
    if (!element) return
    const update = () => {
      const focusNode = document.getSelection()?.focusNode ?? null
      setCaretWithin(focusNode !== null && element.contains(focusNode))
    }
    update()
    document.addEventListener("selectionchange", update)
    // No reset on teardown: `update()` recomputes the truth for whatever element comes
    // next, so a reset could only flip the value on the way out — and a node view whose
    // wrapper is swapped while the caret is inside it would then flip false/true on every
    // swap, each flip scheduling the render that produces the next one (React #185).
    return () => document.removeEventListener("selectionchange", update)
  }, [element])

  const mayShow = arbiter.visible[surface]

  // A popover's pointer-events blanket fires a pointerleave the pointer never earned,
  // and lifting the blanket fires no boundary events at all (Chromium), so a stationary
  // pointer's hover would stay stale-false after the popover closes. Snapshot hover when
  // suppression starts; restore it when suppression ends if the pointer hasn't moved —
  // a pointer that did move produced truthful boundary events already.
  const suppressedSnapshot = useRef<{ x: number; y: number; hovered: boolean } | null>(null)
  useEffect(() => {
    if (!element) return
    if (!mayShow) {
      suppressedSnapshot.current = lastPointerPosition
        ? { ...lastPointerPosition, hovered: hoveredRef.current }
        : null
      return
    }
    const snapshot = suppressedSnapshot.current
    suppressedSnapshot.current = null
    if (
      snapshot &&
      lastPointerPosition &&
      // "Hasn't moved", with sub-pixel tolerance: event coordinates for one physical
      // point can differ by quantization across event types.
      Math.abs(snapshot.x - lastPointerPosition.x) < STATIONARY_TOLERANCE_PX &&
      Math.abs(snapshot.y - lastPointerPosition.y) < STATIONARY_TOLERANCE_PX
    ) {
      setHovered(snapshot.hovered)
    }
  }, [element, mayShow, setHovered])

  // The caret stands in for hover on touch — only read the modality signal when the caret
  // is actually in the region, so a region without the caret takes no dependency on it.
  const caretReveal = caretWithin && interactionModality.touch

  return {
    chromeVisible: isFocusWithin || ((isHovered || caretReveal) && mayShow),
    chromeProps: { ...focusWithinProps, ref: setElement },
  }
}
